// src/services/botService.ts
import axios from "axios";
import { SURVEY_JS_ENDPOINT } from "../config";

interface TokenResponse {
	token: string;
	conversationId: string;
	expires_in: number;
	streamUrl: string;
}

interface RefreshTokenResponse {
	token: string;
	expires_in: number;
	streamUrl: string;
	conversationId: string;
}

class BotService {
	private directLineSecret: string;
	private token: string | null;
	private conversationId: string | null;
	private streamUrl: string | null;
	private tokenExpiryTime: number | null;

	constructor(directLineSecret: string) {
		this.directLineSecret = directLineSecret;
		this.token = null;
		this.conversationId = null;
		this.streamUrl = null;
		this.tokenExpiryTime = null;
	}

	async startConversation(): Promise<void> {
		try {
			var directline;
			const responseConversation = await axios.post<TokenResponse>(
				"https://europe.directline.botframework.com/v3/directline/conversations",
				{},
				{
					headers: {
						Authorization: `Bearer ${this.directLineSecret}`,
					},
				}
			);
			this.token = responseConversation.data.token;
			this.conversationId = responseConversation.data.conversationId;
			this.streamUrl = responseConversation.data.streamUrl;
			this.tokenExpiryTime =
				Date.now() + responseConversation.data.expires_in * 1000;
		} catch (error) {
			console.error("Error starting conversation:", error);
		}
	}

	async refreshToken(): Promise<void> {
		if (!this.token) {
			throw new Error("No token available to refresh");
		}

		try {
			const response = await axios.post<RefreshTokenResponse>(
				`https://europe.directline.botframework.com/v3/directline/tokens/refresh`,
				{},
				{
					headers: {
						Authorization: `Bearer ${this.token}`,
					},
				}
			);

			this.token = response.data.token;
			this.conversationId = response.data.conversationId;
			// this.streamUrl = response.data.streamUrl;
			this.tokenExpiryTime = Date.now() + response.data.expires_in * 1000;
		} catch (error) {
			this.startConversation();
			console.error("Error refreshing token:", error);
		}
	}

	//a function that returns token and stream url
	async getStreamUrl(): Promise<any> {
		try {
			return this;
		} catch (error) {
			console.error("Error getting stream url:", error);
		}
	}

	async ensureTokenIsValid(): Promise<void> {
		if (
			!this.token ||
			!this.tokenExpiryTime ||
			Date.now() > this.tokenExpiryTime - 60000
		) {
			await this.refreshToken();
		}
	}

	async sendMessage(message: string, type: string = "message"): Promise<any> {
		await this.ensureTokenIsValid();
		const response = await axios.post(
			`https://europe.directline.botframework.com/v3/directline/conversations/${this.conversationId}/activities`,
			{
				attachments: [],
				channelId: "directline",
				type: type,
				from: { role: "user", name: "", id: "user" },
				text: message,
				textFormat: "plain",
			},
			{
				headers: {
					Authorization: `Bearer ${this.token}`,
				},
			}
		);
		return response.data;
	}

	async sendFormData(data: any): Promise<any> {
		await this.ensureTokenIsValid();
		const response = await axios.post(
			`https://europe.directline.botframework.com/v3/directline/conversations/${this.conversationId}/activities`,
			{
				attachments: [],
				channelId: "directline",
				type: "message",
				from: { role: "user", name: "", id: "user" },
				value: data,
				textFormat: "plain",
				text: "form submitted",
			},
			{
				headers: {
					Authorization: `Bearer ${this.token}`,
				},
			}
		);
		return response.data;
	}

	async getMessages(): Promise<any> {
		console.log("getting new messages......");

		await this.ensureTokenIsValid();

		const response = await axios.get(
			`https://europe.directline.botframework.com/v3/directline/conversations/${this.conversationId}/activities`,
			{
				headers: {
					Authorization: `Bearer ${this.token}`,
				},
			}
		);
		console.log("response from get messages function", response);
		return response.data.activities;
	}

	async startEndConversationWithType(type: string): Promise<any> {
		await this.ensureTokenIsValid();
		// endOfConversation
		const response = await axios.post(
			`https://europe.directline.botframework.com/v3/directline/conversations/${this.conversationId}/activities`,
			{
				attachments: [],
				channelId: "directline",
				type: type === "endOfConversation" ? "endOfConversation" : "event",
				from: { role: "user", name: "", id: "user" },
				serviceUrl: "https://europe.directline.botframework.com/",
				name: "startConversation",
			},
			{
				headers: {
					Authorization: `Bearer ${this.token}`,
				},
			}
		);
		return response.data;
	}
	async loadReferralForm(
		formEvent: any,
		iframeElement: HTMLIFrameElement
	): Promise<void> {
		const url = SURVEY_JS_ENDPOINT;
		const headers = [
			["Xan-Authenticate", `${formEvent.username}; ${formEvent.TOTP}`],
		];

		console.log("this is the headers", headers);

		const params = `formId=${formEvent.formId}&answers=${encodeURIComponent(
			JSON.stringify(formEvent.answers)
		)}&opt=${formEvent.opt || 0}`;

		console.log("this is the params", params);

		const xhr = new XMLHttpRequest();
		xhr.open("POST", url);
		xhr.setRequestHeader("Content-type", "application/x-www-form-urlencoded");
		xhr.withCredentials = true;
		xhr.responseType = "blob";

		headers.forEach((header) => {
			xhr.setRequestHeader(header[0], header[1]);
		});

		xhr.onreadystatechange = function () {
			if (xhr.readyState === XMLHttpRequest.DONE) {
				if (xhr.status === 200) {
					const blobUrl = URL.createObjectURL(xhr.response);
					iframeElement.src = blobUrl;
				} else {
					console.error("Form loading failed:", xhr);
				}
			}
		};

		xhr.send(params);
	}

	// async endConversationWithType(type: string): Promise<any> {

	//   await this.ensureTokenIsValid();
	//   // endOfConversation
	//   const response = await axios.post(
	//     `https://europe.directline.botframework.com/v3/directline/conversations/${this.conversationId}/activities`,
	//     {
	//       attachments: [],
	//       // channelData: { clientActivityID: 'z4g0zidemso' , attachmentSizes: [] },
	//       channelId: 'directline',
	//       type: 'endOfConversation',
	//       from: { role: 'user',name: '', id: 'user' },
	//       serviceUrl: "https://europe.directline.botframework.com/",
	//     },
	//     {
	//       headers: {
	//         Authorization: `Bearer ${this.token}`,
	//       },
	//     }
	//   );
	//   return response.data;
	// }
}

export default BotService;
